// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import { Table, Button, Icon, Message, Label, Header, Pagination, PaginationProps } from 'semantic-ui-react';
import { brokeragePolicyTypeApi, BrokeragePolicyType } from '../api/brokeragePolicyTypeApi';
import { providerEmailOverridesApi, ProviderEmailOverride } from '../api/providerEmailOverridesApi';

interface PolicyTypeConfigSectionProps {
  brokerageId: string;
  selectedProviderId?: string;
}

function PolicyTypeConfigSection({ brokerageId, selectedProviderId }: Readonly<PolicyTypeConfigSectionProps>) {
  const [brokeragePolicyTypes, setBrokeragePolicyTypes] = useState<BrokeragePolicyType[]>([]);
  const [emailOverrides, setEmailOverrides] = useState<ProviderEmailOverride[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 20;

  const loadData = useCallback(async () => {
    if (!selectedProviderId) {
      setBrokeragePolicyTypes([]);
      setEmailOverrides([]);
      setTotalPages(0);
      setTotalElements(0);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Load brokerage policy type configurations filtered by provider with pagination
      // This API returns BrokeragePolicyType with nested PolicyType details
      const brokeragePolicyTypesData = await brokeragePolicyTypeApi.getByProvider(
        brokerageId,
        selectedProviderId,
        { page: currentPage, size: pageSize }
      );
      setBrokeragePolicyTypes(brokeragePolicyTypesData.content);
      setTotalPages(brokeragePolicyTypesData.totalPages);
      setTotalElements(brokeragePolicyTypesData.totalElements);

      // Load policy type-level email overrides for this brokerage
      // and filter by selectedProviderId
      const emailOverridesData = await providerEmailOverridesApi.getPolicyTypeLevelOverrides(
        brokerageId
      );
      // Filter to only show overrides for the selected provider
      const providerOverrides = emailOverridesData.filter(
        override => override.providerId === selectedProviderId
      );
      setEmailOverrides(providerOverrides);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [brokerageId, selectedProviderId, currentPage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset to first page when provider changes
  useEffect(() => {
    setCurrentPage(0);
  }, [selectedProviderId]);

  const handlePageChange = (_: React.MouseEvent<HTMLAnchorElement>, data: PaginationProps) => {
    const newPage = (data.activePage as number) - 1; // Semantic UI uses 1-based, backend uses 0-based
    setCurrentPage(newPage);
  };

  const handleTogglePolicyType = async (policyTypeId: string, currentlyEnabled: boolean) => {
    try {
      if (currentlyEnabled) {
        await brokeragePolicyTypeApi.disable(brokerageId, policyTypeId);
      } else {
        await brokeragePolicyTypeApi.enable(brokerageId, policyTypeId);
      }
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle policy type');
    }
  };

  const getEmailOverride = (policyTypeId: string): ProviderEmailOverride | undefined => {
    return emailOverrides.find(
      (override) => override.policyTypeId === policyTypeId
    );
  };

  if (!selectedProviderId) {
    return (
      <Message info>
        <Message.Header>Select a Provider</Message.Header>
        <p>Please select a provider from the list to view and configure its policy types.</p>
      </Message>
    );
  }

  return (
    <div>
      <Header as='h3' dividing>
        Policy Types
        <Header.Subheader>Enable policy types and configure settings (Level 1 - Highest Priority)</Header.Subheader>
      </Header>

      {error && (
        <Message negative onDismiss={() => setError(null)}>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Policy Type</Table.HeaderCell>
            <Table.HeaderCell>Category</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
            <Table.HeaderCell>Email</Table.HeaderCell>
            <Table.HeaderCell>LOA Template</Table.HeaderCell>
            <Table.HeaderCell>Email Templates</Table.HeaderCell>
            <Table.HeaderCell textAlign='center'>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {brokeragePolicyTypes.map(config => {
            const policyType = config.policyType;
            if (!policyType) return null;

            const isEnabled = config.isEnabled || false;
            const emailOverride = getEmailOverride(config.policyTypeId);
            const hasEmailTemplates = config.emailSubjectTemplate || config.emailBodyTemplateHtml;

            return (
              <Table.Row key={config.id}>
                <Table.Cell>
                  <Header as='h4'>
                    <Header.Content>
                      {policyType.name}
                      <Header.Subheader>{policyType.description}</Header.Subheader>
                    </Header.Content>
                  </Header>
                </Table.Cell>

                <Table.Cell>
                  <Label>{policyType.category}</Label>
                </Table.Cell>

                <Table.Cell>
                  <Label color={isEnabled ? 'green' : 'grey'}>
                    {isEnabled ? 'Enabled' : 'Disabled'}
                  </Label>
                </Table.Cell>

                <Table.Cell>
                  <div>
                    {emailOverride ? (
                      <span>
                        <Icon name='mail' color='orange' />
                        {emailOverride.overrideEmail}
                        <Label size='mini' color='orange' style={{ marginLeft: '5px' }}>
                          Level 1 Override
                        </Label>
                      </span>
                    ) : (
                      <span style={{ color: '#999' }}>
                        <Icon name='mail outline' color='grey' />
                        Uses provider email
                      </span>
                    )}
                  </div>
                </Table.Cell>

                <Table.Cell>
                  <div>
                    {config.usingDefaultTemplate ? (
                      <span>
                        <Icon name='building' color='grey' />
                        Default
                      </span>
                    ) : (
                      <span>
                        <Icon name='file' color='orange' />
                        {config.customTemplateName || 'Custom'}
                        <Label size='mini' color='orange' style={{ marginLeft: '5px' }}>
                          Level 1
                        </Label>
                      </span>
                    )}
                  </div>
                </Table.Cell>

                <Table.Cell>
                  {hasEmailTemplates ? (
                    <span>
                      <Icon name='envelope' color='orange' />
                      Custom
                      <Label size='mini' color='orange' style={{ marginLeft: '5px' }}>
                        Level 1
                      </Label>
                    </span>
                  ) : (
                    <span style={{ color: '#999' }}>
                      <Icon name='envelope outline' color='grey' />
                      Default
                    </span>
                  )}
                </Table.Cell>

                <Table.Cell textAlign='center' style={{ whiteSpace: 'nowrap' }}>
                  <Button
                    icon={isEnabled ? 'ban' : 'checkmark'}
                    color={isEnabled ? 'red' : 'green'}
                    size='small'
                    title={isEnabled ? 'Disable policy type' : 'Enable policy type'}
                    onClick={() => handleTogglePolicyType(config.policyTypeId, isEnabled)}
                    disabled={loading}
                    style={{ marginRight: '4px' }}
                  />
                  <Button
                    icon='settings'
                    color='blue'
                    size='small'
                    title='Configure policy type settings'
                    onClick={() => {
                      globalThis.location.href = `/brokerages/${brokerageId}/providers/${selectedProviderId}/policy-types/${config.policyTypeId}/edit`;
                    }}
                    disabled={loading}
                  />
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>

      {brokeragePolicyTypes.length === 0 && !loading && (
        <Message info>
          <Message.Header>No Policy Types Available</Message.Header>
          <p>This provider has no policy types configured in the system.</p>
        </Message>
      )}

      {totalPages > 1 && (
        <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ color: '#666' }}>
            Showing {brokeragePolicyTypes.length} of {totalElements} policy types
          </div>
          <Pagination
            activePage={currentPage + 1}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            siblingRange={1}
            boundaryRange={1}
            ellipsisItem={null}
            firstItem={null}
            lastItem={null}
          />
        </div>
      )}
    </div>
  );
}

export default PolicyTypeConfigSection;
